import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronUpIcon, ChevronDownIcon, ShieldCheckIcon, StarIcon } from 'lucide-react';
function LiveViewersBadge() {
  const [viewers, setViewers] = useState(24);
  useEffect(() => {
    const interval = setInterval(() => {
      setViewers(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const next = prev + change * Math.ceil(Math.random() * 3);
        return Math.max(18, Math.min(42, next));
      });
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, []);
  return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 flex items-center gap-2" data-id="element-373">
      <span className="relative flex h-2.5 w-2.5" data-id="element-374">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-375"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" data-id="element-376"></span>
      </span>
      <span className="text-sm font-semibold text-gray-800" data-id="element-377">
        <span className="font-bold text-red-600" data-id="element-378">{viewers}</span> personas están
        viendo esta página
      </span>
    </div>;
}
function TrustBlock({
  open,
  onToggle
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return <div className="mt-6 space-y-4" data-id="element-379">
      {/* Garantía accordion */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-380">
        <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors" data-id="element-381">
          <span className="flex items-center gap-2 font-bold text-gray-900" data-id="element-382">
            <span className="text-lg" data-id="element-383">🏆</span>
            Garantía de satisfacción 7 días
          </span>
          {open ? <ChevronUpIcon className="w-5 h-5 text-gray-500" data-id="element-384" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-385" />}
        </button>
        {open && <div className="px-5 pb-5 text-left text-sm text-gray-600 leading-relaxed" data-id="element-386">
            <p className="mb-3" data-id="element-387">
              Si aplicás el contenido y sentís que no te aporta claridad o no es
              para vos, podés{' '}
              <strong className="text-gray-900" data-id="element-388">
                solicitar una devolución dentro de los primeros 7 días.
              </strong>
            </p>
            <p data-id="element-389">Quiero que compres segura/o, no con dudas.</p>
          </div>}
      </div>

      {/* Pago seguro garantizado */}
      <fieldset className="border border-gray-200 rounded-xl px-5 py-4" data-id="element-390">
        <legend className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-600" data-id="element-391">
          <ShieldCheckIcon className="w-4 h-4 text-brand-green" data-id="element-392" />
          Pago seguro garantizado
        </legend>
        <img src="https://cdn.magicpatterns.com/uploads/hzgvUoMWhjNHxhRDn2LPwT/Imagen_28-3-26_a_las_5.16_p._m._(1).jpg" alt="Métodos de pago: Mercado Pago, Visa, Mastercard, Cabal, American Express, Tarjeta Shopping, Cencosud, Argencard, Provincia NET, Link, Banelco, Pago Fácil, Rapipago, Naranja" className="w-full max-w-sm mx-auto" data-id="element-393" />
      </fieldset>
    </div>;
}
export function PricingSection() {
  const [guaranteeOpen1, setGuaranteeOpen1] = useState(false);
  const [guaranteeOpen2, setGuaranteeOpen2] = useState(false);
  // Countdown timer
  const [timeLeft, setTimeLeft] = useState({
    hours: 1,
    minutes: 36,
    seconds: 39
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        seconds -= 1;
        if (seconds < 0) {
          seconds = 59;
          minutes -= 1;
        }
        if (minutes < 0) {
          minutes = 59;
          hours -= 1;
        }
        if (hours < 0) {
          hours = 0;
          minutes = 0;
          seconds = 0;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <section id="pricing" className="w-full bg-brand-bg pt-6 md:pt-8 pb-8 md:pb-10 px-6" data-id="element-394">
      <div className="max-w-7xl mx-auto" data-id="element-395">
        <motion.div className="flex flex-col lg:flex-row gap-8 items-start" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-396">
          {/* Product image */}
          <div className="w-full lg:w-[65%] flex-shrink-0 flex items-start justify-center" data-id="element-397">
            <img src="https://cdn.magicpatterns.com/uploads/8T5cfanvWDbWYTKJt7Q2U9/11.png" alt="Vende ebooks con Shopify: La guía definitiva" className="w-full h-auto rounded-2xl shadow-xl border border-gray-200" data-id="element-398" />
          </div>

          {/* Pricing card */}
          <div className="w-full lg:w-[35%] bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg shadow-brand-green/5" data-id="element-399">
            <div className="px-6 py-8" data-id="element-400">
              {/* SúperOferta + 50% OFF */}
              <div className="flex items-center gap-3 mb-3" data-id="element-401">
                <span className="inline-block bg-red-500 text-white text-xs font-extrabold px-3 py-1 rounded-md" data-id="element-402">
                  SúperOferta
                </span>
                <span className="text-red-500 font-bold text-sm animate-[pulse_0.8s_ease-in-out_infinite]" data-id="element-403">
                  · 50% OFF
                </span>
              </div>

              {/* Downloads count */}
              <p className="text-sm font-semibold text-gray-500 mb-2" data-id="element-404">
                +2173 DESCARGAS
              </p>

              {/* Product title */}
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-405">
                Vende ebooks con Shopify:{' '}
                <span className="font-normal text-gray-600" data-id="element-406">
                  La guía definitiva
                </span>
              </h3>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-5" data-id="element-407">
                <span className="text-2xl font-bold text-gray-900" data-id="element-408">
                  $24.900
                </span>
                <span className="text-lg text-gray-400 line-through" data-id="element-409">
                  $50.000
                </span>
              </div>

              {/* Divider */}
              <hr className="border-gray-100 mb-5" data-id="element-410" />

              {/* Star rating */}
              <div className="flex items-center gap-1.5 mb-4" data-id="element-411">
                <div className="flex" data-id="element-412">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-413" />)}
                </div>
                <span className="text-sm text-gray-700 font-semibold" data-id="element-414">
                  4.8/5
                </span>
                <span className="text-sm text-gray-500" data-id="element-415">
                  de +1.300 emprendedores
                </span>
              </div>

              {/* Green discount banner */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 mb-4 text-center" data-id="element-416">
                <span className="text-sm font-bold text-black" data-id="element-417">
                  ⏰ DESCUENTO ESPECIAL POR TIEMPO LIMITADO
                </span>
              </div>

              {/* Info box */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-4 mb-4 space-y-2.5" data-id="element-418">
                <p className="text-sm font-bold text-gray-800 flex items-start gap-2" data-id="element-419">
                  <span data-id="element-420">📖</span>
                  CURSO DIGITAL – ACCESO INMEDIATO
                </p>
                <p className="text-sm font-bold text-gray-800 flex items-start gap-2" data-id="element-421">
                  <span data-id="element-422">📩</span>
                  ACCESO AUTOMÁTICO AL FINALIZAR LA COMPRA
                </p>
                <p className="text-sm font-bold text-gray-800 flex items-start gap-2" data-id="element-423">
                  <span data-id="element-424">🔒</span>
                  PAGO 100% SEGURO CON MERCADO PAGO
                </p>
              </div>

              {/* Social proof badge - live viewers */}
              <LiveViewersBadge data-id="element-425" />

              {/* Bonificación activa */}
              <p className="text-center text-sm font-bold text-red-500 mb-4" data-id="element-426">
                🔥 BONIFICACIÓN ACTIVA POR TIEMPO LIMITADO
              </p>

              {/* Countdown timer */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 mb-5 text-center" data-id="element-427">
                <p className="text-sm font-bold text-red-500 mb-1" data-id="element-428">
                  ⏰ ¡Oferta por tiempo limitado!
                </p>
                <p className="text-2xl font-extrabold text-gray-900 tracking-wide" data-id="element-429">
                  {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m{' '}
                  {pad(timeLeft.seconds)}s
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-430">
                  Esta oferta desaparece al terminar el contador.
                </p>
              </div>

              {/* CTA Button */}
              <a href="https://www.emprende-digital.com/cart/51742249156888:1" target="_blank" rel="noopener noreferrer" className="block w-full bg-brand-green hover:bg-brand-green-hover text-white font-bold text-lg py-4 rounded-xl transition-colors duration-200 shadow-lg shadow-brand-green/20 text-center" data-id="element-431">
                🛒 Comprar ahora
              </a>

              {/* Trust block */}
              <TrustBlock open={guaranteeOpen1} onToggle={() => setGuaranteeOpen1(!guaranteeOpen1)} data-id="element-432" />
            </div>
          </div>
        </motion.div>

        {/* Tienda Líder Platinum badge */}
        <motion.div className="mt-8 flex justify-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-433">
          <img src="https://cdn.magicpatterns.com/uploads/6a1pUYDihJzNUtN2uLf8oo/Imagen_29-3-26_a_las_12.25_a._m..jpg" alt="Tienda Líder Platinum — 3758 ventas en los últimos 60 días, brinda un buen servicio, entrega los productos a tiempo" className="w-full max-w-3xl h-auto rounded-2xl shadow-md" data-id="element-434" />
        </motion.div>
      </div>
    </section>;
}